import React, { useState, useEffect } from 'react';
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useAppId } from "../../context/AppIdContext";
import '../../style/boothPramukh.css'
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const BoothOfShaktiKendra = () => {
    const [booths, setBooths] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // --- Mobile Search State ---
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

    // --- Create Pramukh Modal State ---
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [createFormData, setCreateFormData] = useState({
        booth_no: '',
        name: '',
        mobile_no: ''
    });

    const { appId, language, userId } = useAppId();
    const navigate = useNavigate();

    const lang = language || 'en';

    // Safely get user_id from local storage
    const localUserId = localStorage.getItem('userId') || userId || "1";

    const t = {
        en: {
            loadingData: "Loading Data...",
            title: "Shakti Kendra Booths",
            searchPlaceholder: "Search by Booth Number...",
            boothNo: "Booth No.",
            voters: "Voters",
            boothPramukh: "Booth Pramukh",
            notAssigned: "Not Assigned",
            active: "Active",
            inactive: "Not Active",
            viewBtn: "View Details",
            createBtn: "Create Pramukh",
            noResultsPrefix: "No booth found matching",
            noDataAvailable: "No booths available for this Shakti Kendra",
            modalTitle: "Create Booth Pramukh",
            nameLabel: "Full Name",
            mobileLabel: "Mobile Number",
            cancelBtn: "Cancel",
            submitBtn: "Create",
            submitting: "Creating...",
            lastLogin: "Last Login",
            never: "Never"
        },
        hi: {
            loadingData: "डेटा लोड हो रहा है...",
            title: "शक्ति केंद्र बूथ",
            searchPlaceholder: "बूथ नंबर द्वारा खोजें...",
            boothNo: "बूथ नं.",
            voters: "मतदाता",
            boothPramukh: "बूथ प्रमुख",
            notAssigned: "आवंटित नहीं",
            active: "सक्रिय",
            inactive: "निष्क्रिय",
            viewBtn: "विवरण देखें",
            createBtn: "प्रमुख बनाएं",
            noResultsPrefix: "इससे मेल खाने वाला कोई बूथ नहीं मिला",
            noDataAvailable: "इस शक्ति केंद्र के लिए कोई बूथ उपलब्ध नहीं है",
            modalTitle: "बूथ प्रमुख बनाएं",
            nameLabel: "पूरा नाम",
            mobileLabel: "मोबाइल नंबर",
            cancelBtn: "रद्द करें",
            submitBtn: "बनाएं",
            submitting: "बना रहा है...",
            lastLogin: "अंतिम लॉगिन",
            never: "कभी नहीं"
        },
        gu: {
            loadingData: "ડેટા લોડ થઈ રહ્યો છે...",
            title: "શક્તિ કેન્દ્ર બૂથ",
            searchPlaceholder: "બૂથ નંબર દ્વારા શોધો...",
            boothNo: "બૂથ નં.",
            voters: "મતદાર",
            boothPramukh: "બૂથ પ્રમુખ",
            notAssigned: "સોંપેલ નથી",
            active: "Active",
            inactive: "Not Active",
            viewBtn: "વિગતો જુઓ",
            createBtn: "પ્રમુખ બનાવો",
            noResultsPrefix: "કોઈ બૂથ મળ્યું નથી",
            noDataAvailable: "આ શક્તિ કેન્દ્ર માટે કોઈ બૂથ ઉપલબ્ધ નથી",
            modalTitle: "બૂથ પ્રમુખ બનાવો",
            nameLabel: "પૂરું નામ",
            mobileLabel: "મોબાઇલ નંબર",
            cancelBtn: "રદ કરો",
            submitBtn: "બનાવો",
            submitting: "બનાવી રહ્યા છીએ...",
            lastLogin: "છેલ્લું લોગિન",
            never: "ક્યારેય નહિ"
        }
    };

    const txt = t[lang] || t.en;

    useEffect(() => {
        if (appId && localUserId) {
            fetchBooths();
        } else {
            navigate("/");
        }
    }, [appId, localUserId]);

    const fetchBooths = async () => {
        setLoading(true);
        try {
            const payload = {
                app_id: String(appId),
                user_id: String(localUserId)
            };

            const response = await axios.post(`${BASE_URL}/api/dis_booth_pramukh_by_sakti_pramukh`, payload);

            if (response.data.success && response.data.data && response.data.data.result) {
                setBooths(response.data.data.result);
            } else {
                setBooths([]);
            }
        } catch (err) {
            console.error("API Error:", err);
            setBooths([]);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenCreateModal = (boothNo) => {
        setCreateFormData({
            booth_no: boothNo,
            name: '',
            mobile_no: ''
        });
        setIsCreateModalOpen(true);
    };

    const handleCreateInputChange = (e) => {
        const { name, value } = e.target;
        setCreateFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();

        if (!createFormData.name || !createFormData.mobile_no) {
            alert("Please fill in all fields.");
            return;
        }

        if (!/^\d{10}$/.test(createFormData.mobile_no)) {
            alert("Please enter a valid 10-digit mobile number.");
            return;
        }

        setIsSubmitting(true);

        const payload = {
            app_id: String(appId),
            name: createFormData.name,
            mobile_no: createFormData.mobile_no,
            user_type: "BP",
            booth_no: String(createFormData.booth_no),
            booth_list: "",
            create_by: String(localUserId)
        };

        try {
            const response = await axios.post(`${BASE_URL}/api/insert_user`, payload);
            if (response.data.success || response.data.Success === "1" || (response.data.data && response.data.data.Success === "1")) {
                alert("Booth Pramukh created successfully!");
                setIsCreateModalOpen(false);
                setCreateFormData({ booth_no: '', name: '', mobile_no: '' });
                fetchBooths();
            } else {
                alert("Error: " + (response.data.message || "Failed to create user"));
            }
        } catch (error) {
            console.error("API Error:", error);
            alert("Failed to create Booth Pramukh.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const filteredBooths = booths.filter(booth => {
        if (!searchTerm) return true;
        return booth.booth_no?.toString().includes(searchTerm);
    });

    if (loading) {
        return (
            <div className="app-container dashboard-page pb-5">
                <Header title={txt.title} />
                <div className="content-container container-fluid p-3 p-md-4 mx-auto">
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted font-weight-bold">{txt.loadingData}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Create Pramukh Modal */}
            {isCreateModalOpen && (
                <div className="custom-modal-overlay">
                    <div className="custom-modal-box slide-up-element" style={{ animationDuration: '0.2s' }}>
                        <div className="custom-modal-header">
                            <h2>{txt.modalTitle}</h2>
                            <button className="custom-close-btn" onClick={() => setIsCreateModalOpen(false)}>
                                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <form onSubmit={handleCreateSubmit}>
                            <div className="custom-form-body">
                                <div className="custom-input-group">
                                    <label>{txt.boothNo}</label>
                                    <input type="text" value={createFormData.booth_no} disabled style={{ background: '#f3f4f6', cursor: 'not-allowed' }} />
                                </div>
                                <div className="custom-input-group">
                                    <label>{txt.nameLabel}</label>
                                    <input type="text" name="name" value={createFormData.name} onChange={handleCreateInputChange} required />
                                </div>
                                <div className="custom-input-group">
                                    <label>{txt.mobileLabel}</label>
                                    <input type="text" name="mobile_no" value={createFormData.mobile_no} onChange={handleCreateInputChange} maxLength="10" required />
                                </div>
                            </div>
                            <div className="custom-modal-footer">
                                <button type="button" className="btn-secondary" onClick={() => setIsCreateModalOpen(false)}>{txt.cancelBtn}</button>
                                <button type="submit" className="btn-primary" disabled={isSubmitting}>
                                    {isSubmitting ? txt.submitting : txt.submitBtn}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className={`app-container dashboard-page pb-5 ${isCreateModalOpen ? 'blur-background' : ''}`}>

                <Header title={txt.title} />

                <div className="content-container container-fluid p-3 p-md-4 mx-auto">

                    

                    {/* User Grid - Same as BoothPramukh page */}
                    <div className="row g-4 slide-up-element" style={{ animationDelay: '0.1s' }}>
                        {filteredBooths.length > 0 ? (
                            filteredBooths.map((booth, index) => {
                                const isAssigned = booth.name !== null && booth.name !== "";
                                const isActive = booth.last_login !== null && booth.last_login !== "";

                                return (
                                    <div className="col-12 col-sm-6 col-lg-4 col-xl-3" key={index}>
                                        <div className="bp-card" onClick={() => { if (isAssigned) navigate('/booth_pramukh_detail_with_voters', { state: { selectedBoothNo: booth.booth_no } }) }}>

                                            {/* Top Light Green Section */}
                                            <div className="bp-card-header">
                                                <div className="bp-avatar">
                                                    {booth.photo_path ? (
                                                        <img src={booth.photo_path} alt={booth.name} />
                                                    ) : (
                                                        <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                                                            <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                                        </svg>
                                                    )}
                                                </div>
                                                <div className="bp-header-info">
                                                    <h3 className="bp-booth-no">{txt.boothNo} {booth.booth_no}</h3>
                                                    <p className="bp-voter-count">{txt.voters} : {booth.total_voter || 0}</p>
                                                </div>
                                            </div>

                                            {/* Middle Details Section */}
                                            <div className="bp-card-body">
                                                <span className="bp-label">{txt.boothPramukh}</span>
                                                <h4 className={`bp-name ${!isAssigned ? 'text-muted' : ''}`}>
                                                    {isAssigned ? booth.name : txt.notAssigned}
                                                </h4>

                                                {isAssigned ? (
                                                    <div className={`bp-status-pill ${isActive ? 'active' : 'inactive'}`}>
                                                        {isActive ? txt.active : txt.inactive}
                                                    </div>
                                                ) : (
                                                    <div className="bp-status-pill unassigned" style={{ visibility: 'hidden' }}>Placeholder</div>
                                                )}
                                            </div>

                                            {/* Bottom Actions Section */}
                                            <div className="bp-card-footer">
                                                {isAssigned ? (
                                                    <>
                                                        <button className="bp-action-btn text-primary" onClick={(e) => { e.stopPropagation(); window.open(`tel:${booth.mobile_no}`); }}>
                                                            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                                                        </button>
                                                        <button className="bp-action-btn text-success" onClick={(e) => { e.stopPropagation(); window.open(`sms:${booth.mobile_no}`); }}>
                                                            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                                                        </button>
                                                        <button className="bp-action-btn text-warning" onClick={(e) => { e.stopPropagation(); navigate('/booth_pramukh_detail_with_voters', { state: { selectedBoothNo: booth.booth_no } }); }}>
                                                            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button className="bp-create-btn" onClick={(e) => { e.stopPropagation(); handleOpenCreateModal(booth.booth_no); }}>
                                                        {txt.createBtn}
                                                    </button>
                                                )}
                                            </div>

                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="col-12">
                                <div className="text-center text-muted p-5 bg-white rounded shadow-sm border">
                                    <div style={{ fontSize: '40px', marginBottom: '16px' }}>📭</div>
                                    <h4>{searchTerm ? `${txt.noResultsPrefix} "${searchTerm}"` : txt.noDataAvailable}</h4>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <style jsx>{`
                /* --- BLUR EFFECT --- */
                .blur-background { filter: blur(4px); pointer-events: none; transition: filter 0.3s ease; }

                
                .header-container-white {
                    background-color: #ffffff;
                    padding: 15px 32px;
                    border-bottom: 1px solid #e2e8f0;
                    margin: 0px 0px 10px 0px; 
                    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                    position: sticky;
                    top: 0;
                    z-index: 10;
                }
                .header-wrapper { background: white; padding: 0px 24px; position: sticky; top: 0; z-index: 50; }
                
                
                /* Search Bar */
                .filter-container {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 12px;
                }
                .search-wrapper { position: relative; width: 100%; }
                .search-icon { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); color: #94a3b8; width: 20px; height: 20px; }
                .search-input { width: 100%; padding: 14px 40px 14px 46px; border: 1px solid #cbd5e1; border-radius: 12px; font-size: 15px; color: #1e293b; background-color: #ffffff; outline: none; transition: all 0.2s ease; box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
                .search-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }

                /* Card Styling - Same as BoothPramukh */
                .bp-card {
                    background: #ffffff;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                    height: 100%;
                    cursor: pointer;
                }
                .bp-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 20px rgba(0,0,0,0.08);
                }

                /* Light Green Top Header */
                .bp-card-header {
                    background-color: #dcfce7;
                    padding: 16px 20px;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }
                .bp-avatar {
                    width: 56px;
                    height: 56px;
                    border-radius: 50%;
                    background: #a1a1aa;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                    flex-shrink: 0;
                }
                .bp-avatar img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }
                .bp-header-info {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                .bp-booth-no {
                    margin: 0;
                    font-size: 18px;
                    font-weight: 800;
                    color: #1c1917;
                }
                .bp-voter-count {
                    margin: 0;
                    font-size: 15px;
                    font-weight: 700;
                    color: #16a34a;
                }

                /* Body / Name Section */
                .bp-card-body {
                    padding: 24px 20px;
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    flex-grow: 1;
                }
                .bp-label {
                    font-size: 13px;
                    font-weight: 700;
                    color: #64748b;
                    margin-bottom: 6px;
                }
                .bp-name {
                    font-size: 20px;
                    font-weight: 800;
                    color: #0f172a;
                    margin: 0 0 16px 0;
                    line-height: 1.2;
                }
                
                /* Status Pills */
                .bp-status-pill {
                    padding: 8px 24px;
                    border-radius: 30px;
                    font-size: 14px;
                    font-weight: 700;
                    display: inline-block;
                }
                .bp-status-pill.active {
                    background-color: #dcfce7;
                    color: #166534;
                }
                .bp-status-pill.inactive { 
                    background-color: #fee2e2;
                    color: #991b1b;
                }

                /* Footer Actions */
                .bp-card-footer {
                    display: flex;
                    justify-content: space-around;
                    align-items: center;
                    padding: 16px;
                    border-top: 1px solid #e2e8f0;
                    background: #ffffff;
                }
                .bp-action-btn {
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 8px;
                    border-radius: 50%;
                    transition: background 0.2s;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .bp-action-btn:hover:not(:disabled) {
                    background: #f1f5f9;
                }
                .bp-create-btn {
                    width: 100%;
                    padding: 10px;
                    background: #991b1b;
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-weight: 600;
                    font-size: 14px;
                    cursor: pointer;
                    transition: 0.2s;
                }
                .bp-create-btn:hover { background: #2563eb; }

                /* Modal Styles */
                .custom-modal-overlay { 
                    position: fixed; 
                    top: 0; 
                    left: 0; 
                    right: 0; 
                    bottom: 0; 
                    background: rgba(15, 23, 42, 0.75); 
                    backdrop-filter: blur(4px); 
                    display: flex; 
                    align-items: center; 
                    justify-content: center; 
                    z-index: 10000; 
                    padding: 20px; 
                    box-sizing: border-box; 
                }
                .custom-modal-box { 
                    background: white; 
                    border-radius: 16px; 
                    width: 100%; 
                    max-width: 420px; 
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); 
                    overflow: hidden; 
                }
                .custom-modal-header { 
                    padding: 20px 24px; 
                    border-bottom: 1px solid #f1f5f9; 
                    display: flex; 
                    justify-content: space-between; 
                    align-items: center; 
                }
                .custom-modal-header h2 { 
                    margin: 0; 
                    font-size: 1.25rem; 
                    color: #0f172a; 
                    font-weight: 700; 
                }
                .custom-close-btn { 
                    background: none; 
                    border: none; 
                    color: #94a3b8; 
                    cursor: pointer; 
                }
                .custom-form-body { 
                    padding: 24px; 
                    display: flex; 
                    flex-direction: column; 
                    gap: 16px; 
                }
                .custom-input-group { 
                    display: flex; 
                    flex-direction: column; 
                    gap: 6px; 
                }
                .custom-input-group label { 
                    font-size: 13px; 
                    font-weight: 600; 
                    color: #475569; 
                }
                .custom-input-group input { 
                    padding: 12px 16px; 
                    border: 1px solid #cbd5e1; 
                    border-radius: 8px; 
                    font-size: 14px; 
                    outline: none; 
                }
                .custom-modal-footer { 
                    padding: 16px 24px; 
                    background: #f8fafc; 
                    border-top: 1px solid #f1f5f9; 
                    display: flex; 
                    justify-content: flex-end; 
                    gap: 12px; 
                }
                .btn-secondary { 
                    padding: 10px 16px; 
                    border: 1px solid #cbd5e1; 
                    background: white; 
                    color: #475569; 
                    border-radius: 8px; 
                    font-weight: 600; 
                    cursor: pointer; 
                }
                .btn-primary { 
                    padding: 10px 20px; 
                    border: none; 
                    background: #2563eb; 
                    color: white; 
                    border-radius: 8px; 
                    font-weight: 600; 
                    cursor: pointer; 
                }

                /* Animations */
                .slide-up-element { animation: slideUp 0.4s ease forwards; opacity: 0; }
                @keyframes slideUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }

                /* Responsive */
                @media (max-width: 768px) {
                    .bp-card-header {
                        padding: 12px 16px;
                    }
                    .bp-avatar {
                        width: 44px;
                        height: 44px;
                    }
                    .bp-booth-no {
                        font-size: 16px;
                    }
                    .bp-voter-count {
                        font-size: 13px;
                    }
                    .bp-name {
                        font-size: 18px;
                    }
                    .bp-status-pill {
                        padding: 6px 18px;
                        font-size: 12px;
                    }
                }

                @media (max-width: 640px) {
                    .filter-container {
                        flex-direction: column;
                    }
                    .search-wrapper {
                        width: 100%;
                    }
                    .search-input {
                        padding: 12px 36px 12px 42px;
                        font-size: 13px;
                    }
                }
            `}</style>
        </>
    );
};

export default BoothOfShaktiKendra;